import * as SliderPrimitive from '@radix-ui/react-slider'
// import { DollarSign } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'

import { RoundTriangle } from 'src/shared/components/icons/round-triangle'
import { Input } from 'src/shared/components/ui/inputs/input'
import CustomCheckbox from 'src/shared/features/users/components/marketplace/auth-forms/custom-checkbox'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'
import { formatCurrency, formatCurrencyWithDollarSign, parseCurrencyToNumber } from 'src/shared/utils/format-currency'

interface PriceRangeSliderProps {
  field: {
    value: number[]
    onChange: (value: number[]) => void
  }
  min: number
  max: number
  step: number
  className?: string
  disabled?: boolean
  formatMaxValue?: (value: number) => string | undefined
  inputClassName?: string
  trackClassName?: string
  thumbClassName?: string
  bubbleClassName?: string
  showValueBubbles?: boolean
  inputsJustify?: 'between' | 'start' | 'end' | 'center'
  hideInputs?: boolean
  showNotSure?: boolean
  notSureText?: string
  notSureField?: {
    value: boolean
    onChange: (value: boolean) => void
  }
  inputContainerClassName?: string
  sliderClassName?: string
  valueBubblesType?: 'bubble' | 'text'
}

export const PriceRangeSlider = ({
  field,
  min,
  max,
  step,
  className,
  disabled,
  inputClassName,
  formatMaxValue,
  trackClassName,
  thumbClassName,
  bubbleClassName,
  showValueBubbles = false,
  inputsJustify = 'between',
  hideInputs = false,
  showNotSure = false,
  notSureText = 'Not sure yet',
  notSureField,
  inputContainerClassName,
  sliderClassName,
  valueBubblesType = 'bubble',
}: PriceRangeSliderProps) => {
  const [localValue, setLocalValue] = useState<[number, number]>([min, max])
  const [isNotSure, setIsNotSure] = useState(notSureField?.value || false)
  const isInitialRender = useRef(true)
  const isUpdatingFromNotSure = useRef(false)
  const { isMobile } = useBreakpoint()

  // Sync local value with field value
  useEffect(() => {
    if (Array.isArray(field.value) && field.value.length === 2 && !isUpdatingFromNotSure.current) {
      setLocalValue(field.value as [number, number])
    }
  }, [field.value])

  // Sync local notSure state with prop
  useEffect(() => {
    if (notSureField && notSureField.value !== isNotSure) {
      setIsNotSure(notSureField.value)
    }
  }, [notSureField?.value])

  // Handle "Not sure" changes and update range accordingly
  useEffect(() => {
    // Skip this effect on initial render
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }

    if (isNotSure) {
      isUpdatingFromNotSure.current = true
      const fullRangeValue: [number, number] = [min, max]
      setLocalValue(fullRangeValue)
      field.onChange(fullRangeValue)
      setTimeout(() => {
        isUpdatingFromNotSure.current = false
      }, 0)
    }
  }, [isNotSure, min, max])

  const handleSliderChange = (newValue: number[]) => {
    const validValue: [number, number] = [newValue[0] || min, newValue[1] || max]
    setLocalValue(validValue)
    field.onChange(validValue)
  }

  const handleInputChange = (index: number, value: string) => {
    const numValue = parseCurrencyToNumber(value) || 0
    const newValue: [number, number] = [...localValue] as [number, number]
    newValue[index] = Math.max(min, Math.min(max, numValue))
    setLocalValue(newValue)
    field.onChange(newValue)
  }

  const handleNotSureChange = (checked: boolean) => {
    setIsNotSure(checked)
    if (notSureField) {
      notSureField.onChange(checked)
    }
  }

  const formatValue = (value: number, index: number) => {
    const currency = '$'
    if (index === 1 && formatMaxValue) {
      const formatted = formatMaxValue(value)
      if (formatted) return currency + formatted
    }
    const formattedValue = formatCurrency(value)
    if (formattedValue !== '') {
      return currency + formattedValue
    }
    return formattedValue
  }

  const calculateBubblePosition = (value: number) => {
    const percentage = ((value - min) / (max - min)) * 100
    return `calc(${percentage}% + (${8 - percentage * 0.15}px))`
  }

  const formatBubbleCurrency = (value: number) => {
    return formatCurrency(value)
  }

  const isSliderDisabled = disabled || isNotSure

  return (
    <div className={cn('space-y-6', className)}>
      <div className={cn('relative pt-7', showValueBubbles && 'pt-10', sliderClassName)}>
        {showValueBubbles && !isNotSure && (
          <>
            {valueBubblesType === 'bubble' && (
              <>
                <div
                  className={cn('price-bubble absolute bottom-9 z-10 flex flex-col items-center', bubbleClassName)}
                  style={{ left: calculateBubblePosition(localValue[0]), transform: 'translateX(-50%)' }}
                >
                  <div
                    className={cn(
                      'bubble-content rounded-md bg-primary p-[10px] text-sm font-medium text-white',
                      'relative',
                    )}
                  >
                    <span className="flex items-center font-semibold">
                      <span className="mr-0.5 text-xs">$</span>
                      {formatBubbleCurrency(localValue[0])}
                    </span>
                    <RoundTriangle className="absolute left-1/2 top-full size-4 -translate-x-1/2 -translate-y-1/3 text-primary" />
                    {/* <div className="absolute left-1/2 top-full -ml-1.5 size-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-primary" /> */}
                  </div>
                </div>
                <div
                  className={cn('price-bubble absolute bottom-9 z-10 flex flex-col items-center', bubbleClassName)}
                  style={{ left: calculateBubblePosition(localValue[1]), transform: 'translateX(-50%)' }}
                >
                  <div
                    className={cn(
                      'bubble-content rounded-md bg-primary p-[10px] text-sm font-medium text-white',
                      'relative',
                    )}
                  >
                    <span className="flex items-center font-semibold">
                      <span className="mr-0.5 text-xs">$</span>
                      {formatBubbleCurrency(localValue[1])}
                    </span>
                    <RoundTriangle className="absolute left-1/2 top-full size-4 -translate-x-1/2 -translate-y-1/3 text-primary" />
                    {/* <div className="absolute left-1/2 top-full -ml-1.5 size-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-primary" /> */}
                  </div>
                </div>
              </>
            )}
            {valueBubblesType === 'text' && !isMobile && (
              <>
                <div
                  className={cn('price-bubble absolute bottom-5 z-10 flex flex-col items-center', bubbleClassName)}
                  style={{ left: calculateBubblePosition(localValue[0]), transform: 'translateX(-50%)' }}
                >
                  <span className="flex items-center text-[20px] font-semibold md:text-[26px]">
                    {formatCurrencyWithDollarSign(localValue[0], { numberOfDecimalsForM: 2 })}
                  </span>
                </div>
                <div
                  className={cn('price-bubble absolute bottom-5 z-10 flex flex-col items-center', bubbleClassName)}
                  style={{ left: calculateBubblePosition(localValue[1]), transform: 'translateX(-50%)' }}
                >
                  <span className="flex items-center text-[20px] font-semibold md:text-[26px]">
                    {`${formatCurrencyWithDollarSign(localValue[1], { numberOfDecimalsForM: 2 })}${
                      localValue[1] >= max ? '+' : ''
                    }`}
                  </span>
                </div>
              </>
            )}
          </>
        )}
        <div className="relative flex w-full touch-none select-none items-center">
          <SliderPrimitive.Root
            min={min}
            max={max}
            step={step}
            value={localValue}
            disabled={isSliderDisabled}
            onValueChange={handleSliderChange}
            className="relative flex w-full touch-none select-none items-center"
          >
            <SliderPrimitive.Track
              className={cn(
                'relative h-[7px] w-full grow overflow-hidden rounded-full bg-border md:h-[8px]',
                trackClassName,
              )}
            >
              <SliderPrimitive.Range className={cn('absolute h-full bg-primary', isSliderDisabled && 'bg-muted')} />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb
              className={cn(
                'block size-[12px] cursor-pointer rounded-full bg-primary shadow-[0_0px_20px_2px_rgba(27,48,41,0.15)] outline outline-[3px] outline-white transition-colors disabled:pointer-events-none disabled:opacity-50 md:size-[16px] md:outline-[3px]',
                thumbClassName,
                isSliderDisabled && 'bg-white outline-muted',
              )}
            />
            <SliderPrimitive.Thumb
              className={cn(
                'block size-[12px] cursor-pointer rounded-full bg-primary shadow-[0_0px_20px_2px_rgba(27,48,41,0.15)] outline outline-[3px] outline-white transition-colors disabled:pointer-events-none disabled:opacity-50 md:size-[16px] md:outline-[3px]',
                thumbClassName,
                isSliderDisabled && 'bg-white outline-muted',
              )}
              // style={{ boxShadow: '0 1px 20px 1px rgba(27, 48, 41, 0.15)' }}
            />
          </SliderPrimitive.Root>
        </div>
      </div>

      {isMobile && !isNotSure && (
        <div className="flex flex-row items-center justify-center gap-10">
          <div className="flex flex-col items-center">
            <p className="text-[15px] text-muted">Min</p>
            <span className="flex items-center text-[26px] font-semibold tracking-[-0.26px]">
              {isNotSure
                ? formatValue(localValue[0], 0)
                : formatCurrencyWithDollarSign(localValue[0], { numberOfDecimalsForM: 2 })}
            </span>
          </div>
          <div className="h-[30px] w-px shrink-0 bg-[#D8D8D6]"></div>
          <div className="flex flex-col items-center">
            <p className="text-[15px] text-muted">Max</p>
            <span className="flex items-center text-[26px] font-semibold tracking-[-0.26px]">
              {isNotSure
                ? formatValue(localValue[1], 1)
                : `${formatCurrencyWithDollarSign(localValue[1], { numberOfDecimalsForM: 2 })}${
                    localValue[1] >= max ? '+' : ''
                  }`}
            </span>
          </div>
        </div>
      )}

      {(!hideInputs || showNotSure) && (
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 md:justify-between">
          {!hideInputs && (
            <div
              className={cn(
                'flex items-center gap-2 xs:gap-4 md:gap-8',
                {
                  'justify-between': inputsJustify === 'between',
                  'justify-start': inputsJustify === 'start',
                  'justify-end': inputsJustify === 'end',
                  'justify-center': inputsJustify === 'center',
                },
                inputContainerClassName,
              )}
            >
              <div className="relative">
                {/* <DollarSign className="absolute left-3 top-1/2 z-20 size-5 -translate-y-1/2 text-muted" /> */}
                <Input
                  type="text"
                  value={formatValue(localValue[0], 0)}
                  disabled={isSliderDisabled}
                  onChange={(e) => handleInputChange(0, e.target.value.replace('$', ''))}
                  className={cn(
                    'w-full rounded-full text-center disabled:cursor-not-allowed disabled:opacity-50',
                    inputClassName,
                  )}
                />
              </div>
              <p>-</p>
              <div className="relative">
                {/* <DollarSign className="absolute left-3 top-1/2 z-20 size-5 -translate-y-1/2 text-muted" /> */}
                <Input
                  type="text"
                  value={formatValue(localValue[1], 1)}
                  disabled={isSliderDisabled}
                  onChange={(e) => handleInputChange(1, e.target.value.replace('$', ''))}
                  className={cn(
                    'w-full rounded-full text-center disabled:cursor-not-allowed disabled:opacity-50',
                    inputClassName,
                  )}
                />
              </div>
            </div>
          )}
          {showNotSure && (
            <div className={cn('flex items-center space-x-2 pt-2 sm:pt-0', hideInputs && 'w-full justify-center')}>
              {/* <Checkbox
                id="price_range_not_sure"
                checked={isNotSure}
                onCheckedChange={(checked) => {
                  handleNotSureChange(!!checked)
                }}
              /> */}
              <CustomCheckbox
                id="price_range_not_sure"
                checked={isNotSure}
                onChange={handleNotSureChange}
                className="size-6"
              />
              <label
                htmlFor="price_range_not_sure"
                className="text-[16px] font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 md:text-[18px]"
              >
                {notSureText}
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
